import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useCurrent } from '../hooks';

const AccountSettingsContext = createContext();

export const AccountSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { currentTenantId, currentCompanyId } = useCurrent();
  const fetchedKeyRef = useRef(null);
  const loadingKeyRef = useRef(null);
  const settingsRef = useRef(null);

  const updateSettings = useCallback((nextSettings) => {
    settingsRef.current = nextSettings;
    setSettings(nextSettings);
  }, []);

  const requestSettings = useCallback(async (tenantId, companyId, forceRefresh = false) => {
    if (!tenantId || !companyId) return;

    const requestKey = `${tenantId}:${companyId}`;
    if (!forceRefresh) {
      if (loadingKeyRef.current === requestKey) return;
      if (fetchedKeyRef.current === requestKey && settingsRef.current) return;
    }

    loadingKeyRef.current = requestKey;
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        'https://api.absat-erp.com/acc/setting/get-all',
        {
          tenant_id: tenantId,
          company_id: companyId,
        },
        {
          headers: token
            ? {
                'Content-Type': 'application/json; charset=utf-8',
                Authorization: `Bearer ${token}`,
              }
            : {
                'Content-Type': 'application/json; charset=utf-8',
              }
        }
      );

      if (response?.data?.data) {
        updateSettings(response.data.data);
        fetchedKeyRef.current = requestKey;
        return;
      }

      setError(response?.data?.message || 'Failed to load settings');
    } catch (requestError) {
      setError(requestError?.message || 'Failed to load settings');
    } finally {
      loadingKeyRef.current = null;
      setLoading(false);
    }
  }, [updateSettings]);

  useEffect(() => {
    requestSettings(currentTenantId, currentCompanyId, false);
  }, [currentCompanyId, currentTenantId, requestSettings]);

  const getChartAccountLevelLimit = () => {
    return settings?.chart_account_level_limit ? parseInt(settings.chart_account_level_limit) : null;
  };

  const getCostCenterLevelLimit = () => {
    return settings?.cost_center_level_limit ? parseInt(settings.cost_center_level_limit) : null;
  };

  const refreshSettings = useCallback(() => {
    fetchedKeyRef.current = null;
    requestSettings(currentTenantId, currentCompanyId, true);
  }, [currentCompanyId, currentTenantId, requestSettings]);

  return (
    <AccountSettingsContext.Provider
      value={{
        settings,
        loading,
        error,
        getChartAccountLevelLimit,
        getCostCenterLevelLimit,
        refreshSettings,
      }}
    >
      {children}
    </AccountSettingsContext.Provider>
  );
};

export const useAccountSettings = () => {
  const context = useContext(AccountSettingsContext);
  if (!context) {
    throw new Error('useAccountSettings must be used within AccountSettingsProvider');
  }
  return context;
};

export default AccountSettingsContext;
